import type { Site } from "@/types/sites";
import type { OfferPageTemplate } from "@/types/offers";
import type { LandingPageTemplateResponse } from "@/types/landing";
import {
    setCookie,
    getClientCookie,
    getFacebookCookies,
} from "@/utils/cookies/client";
import {
    DEFAULT_AUTHENTICATED_STRING,
    FORM_DATA_COOKIE_NAME,
    LANDING_PAGE_COMPLETE_COOKIE_NAME,
    LANDING_PAGE_TEMPLATE_CACHE_KEY_COOKIE_NAME,
    LANDING_PAGE_URL_COOKIE_NAME,
    SKIP_LEAD_FORM_COOKIE_NAME,
} from "@/constants";
import { SyntheticEvent } from "react";
import {
    buttonToggle,
    emailRequiredToggle,
    hashStringFrontend,
    LEAD_FORM_SUBMITTED_EVENT_NAME,
    logNewGoogleAnalyticsEvent,
} from "@/utils/utils";
import { sendBeaconOrFetch } from "../../../utils/apiUtils";
import type { SessionType } from "@/types/nextAuth";
import { signOut } from "next-auth/react";
import { isPrefetch } from "@/lib/prefetch";
import { isServerSide } from "@/utils/ssr";
import { UserCookie } from "@/types/nextjs";

export const leadFormId = "lead-form";

export const getOfferSlugFromID = (
    site: Site | null,
    offerID: number | undefined
): OfferPageTemplate | null => {
    const offerPageTemplates = site?.attributes?.offer_page_templates?.data;

    const slugOfferPage = offerPageTemplates?.find(
        (page: OfferPageTemplate) => {
            return page.id === offerID;
        }
    );

    return slugOfferPage || null;
};

export const getOfferPageRedirectPath = (
    site: Site | null,
    landingPage: LandingPageTemplateResponse | undefined,
    offerData: OfferPageTemplate | undefined
): string => {
    const { origin } = window.location;
    const { attributes: landingPageAttributes } = landingPage?.data || {};
    const { attributes: offerDataAttributes } = offerData || {};

    const skipLeadOfferData: OfferPageTemplate | null = getOfferSlugFromID(
        site,
        landingPageAttributes?.skip_offer_page_id ?? undefined
    );

    const skipCookie = getClientCookie(SKIP_LEAD_FORM_COOKIE_NAME);
    const skipCookieBoolean: boolean = skipCookie === "true";

    if (skipCookieBoolean) {
        const skipOfferSlug = skipLeadOfferData?.attributes?.offerSlug || "";
        return `${origin}/${skipOfferSlug}`;
    }

    if (offerDataAttributes?.offerSlug) {
        // 02/08/2024 - Updated to use relative path instead of an absolute path
        // This is to fix an issue when the site is served behind a reverse proxy
        // The issue occurs because `window.location.origin` does not reflect the actual
        // URL that the client sees.
        // return `${origin}/${offerDataAttributes.offerSlug}`;
        return `/${offerDataAttributes.offerSlug}`;
    }

    return "/";
};

const setSkipButtonCookie = (skipButtonClicked: boolean | undefined) => {
    setCookie(
        SKIP_LEAD_FORM_COOKIE_NAME,
        skipButtonClicked,
        60000 * 60 * 24 * 30
    );
};

const getUserFormData = (skipButtonClicked: boolean) => {
    const form = document.querySelector<HTMLFormElement>(`#${leadFormId}`);
    if (!form) {
        return;
    }
    const userFormData = new FormData(form);

    const userFormDataEntries = Object.fromEntries(userFormData.entries());
    userFormDataEntries.provider = skipButtonClicked ? "skip" : "email";

    return userFormDataEntries;
};

export const logLeadAnalyticsEvent = (
    site: Site | null,
    provider: string | undefined,
    landingPage?: string,
    userEmail?: string,
    rtid?: string | null,
    lavi?: string | null
) => {
    if (site?.attributes?.googleTagManagerContainerId && provider) {
        const leadEventCustomLabels: Record<string, string> = {
            provider: provider,
        };
        if (rtid) {
            leadEventCustomLabels["rtid"] = rtid;
        }
        if (userEmail) {
            leadEventCustomLabels["email"] = userEmail;
        }
        if (lavi) {
            leadEventCustomLabels["lavi"] = lavi;
        }
        logNewGoogleAnalyticsEvent(
            LEAD_FORM_SUBMITTED_EVENT_NAME,
            leadEventCustomLabels,
            landingPage
        );
    }
};

const setLandingPageCompleteCookie = (skipButtonClicked: boolean) => {
    if (!skipButtonClicked) {
        setCookie(LANDING_PAGE_COMPLETE_COOKIE_NAME, true);
    }
};

export const getRedirectUrl = (
    site: Site | null,
    landingPage: LandingPageTemplateResponse | null,
    prefetch = false
): string | null => {
    if (isServerSide()) {
        return null;
    }

    const offerData: OfferPageTemplate | null = getOfferSlugFromID(
        site,
        landingPage?.data?.attributes?.offer_page_id
    );
    const offerPageRedirectPath = getOfferPageRedirectPath(
        site,
        landingPage ?? undefined,
        offerData ?? undefined
    );
    const offerPageRedirectUrl = `${window.location.origin}${offerPageRedirectPath}`;

    if (prefetch) {
        const prefetchUrl = new URL(offerPageRedirectUrl);
        prefetchUrl.searchParams.set("prefetch", "true");
        return prefetchUrl.toString();
    }

    return offerPageRedirectUrl;
};

export const handleFormSubmit = async (
    e:
        | SyntheticEvent<HTMLFormElement, SubmitEvent>
        | SyntheticEvent<HTMLButtonElement>,
    site: Site | null,
    landingPage?: LandingPageTemplateResponse | null,
    skipButtonClicked?: boolean,
    userCookie?: UserCookie
): Promise<string | undefined> => {
    e.preventDefault();

    const offerData: OfferPageTemplate | null = getOfferSlugFromID(
        site,
        landingPage?.data?.attributes?.offer_page_id
    );

    setSkipButtonCookie(skipButtonClicked || undefined);

    const formTarget: HTMLFormElement = e.target as HTMLFormElement;
    emailRequiredToggle(formTarget, true);

    const offerPageRedirectPath = getOfferPageRedirectPath(
        site,
        landingPage ?? undefined,
        offerData ?? undefined
    );

    const offerPageRedirectUrl = `${window.location.origin}${offerPageRedirectPath}`;

    if (formTarget.checkValidity() || skipButtonClicked) {
        buttonToggle(formTarget, true);

        const userFormDataEntries = getUserFormData(skipButtonClicked || false);
        const provider = userFormDataEntries?.provider?.toString();

        const userEmail = userFormDataEntries?.email
            ? await hashStringFrontend(userFormDataEntries.email.toString())
            : undefined;

        const searchParmas = new URLSearchParams(window.location.search);
        const rtid = searchParmas.get("rtid");
        const twclid = searchParmas.get("twclid");

        logLeadAnalyticsEvent(
            site,
            provider,
            undefined,
            userEmail,
            rtid,
            userCookie?.lastAvailableVisitorId
        );
        setLandingPageCompleteCookie(skipButtonClicked || false);

        const { fbp, fbc } = getFacebookCookies();

        const allData = {
            href: window.location.href,
            site,
            landingPage,
            userFormData: userFormDataEntries,
            offerPageRedirectUrl,
            ckm_request_id: window.ckm_request_id,
            fbp: fbp,
            fbc: fbc,
            twclid: twclid,
            origin: "form",
        };

        try {
            await sendBeaconOrFetch(
                "/api/form/leadForm",
                allData,
                "leadForm-form"
            );

            return offerPageRedirectPath;
        } catch (error) {
            console.error(error);
        }
    }

    return undefined;
};

type FormData = {
    email?: string;
    provider?: string;
    [key: string]: string | undefined;
};

type SubmitFromSocialsFunctionType = (
    site: Site | null,
    sessionData: SessionType | null,
    status: string,
    offerPageRedirectUrl?: string
) => void;

export const submitFromSocials: SubmitFromSocialsFunctionType = (
    site,
    sessionData,
    status,
    offerPageRedirectUrl
) => {
    if (sessionData && status === DEFAULT_AUTHENTICATED_STRING) {
        setCookie(LANDING_PAGE_COMPLETE_COOKIE_NAME, true);

        // Extracting user email & provider from session for overall data
        const session: SessionType = sessionData;
        const sessionEmail = session.user?.email || "";
        const sessionProvider = session.provider || "";

        // As we are storing the users form data in a cookie, we need to update the email as it wasn't
        // captured at the time of saving
        let cookieFormData: FormData = JSON.parse(
            getClientCookie(FORM_DATA_COOKIE_NAME)
        );

        // When submitting from socials, we are doing this from the offer page, but we need the landing
        // page for the data in BigQuery. We can't store the whole landing page object in the redirect URL
        // or a cookie, so we must retrieve it back another way.
        // By storing the cache key, we can retrieve the landing page from the cache in the backend
        // This is nearly always guaranteed to return the landing page
        // since it was just visited, but there are some edge cases (e.g if the cache is cleared between the user
        // visiting the landing page and submitting the form) where it could return null.
        const landingPageCacheKey = getClientCookie(
            LANDING_PAGE_TEMPLATE_CACHE_KEY_COOKIE_NAME
        );

        const source = getClientCookie(LANDING_PAGE_URL_COOKIE_NAME);

        // source is the URL of the landing page
        // e.g https://www.example.com/landing-page?twclid=1234
        let twclid = null;
        if (source != "{}") {
            const url = new URL(source);
            const params = new URLSearchParams(url.search);
            twclid = params.get("twclid");
        }

        if (cookieFormData.email !== undefined) {
            cookieFormData.email = sessionEmail;
            cookieFormData.provider = sessionProvider;
        } else {
            cookieFormData = {
                email: sessionEmail,
                provider: sessionProvider,
            };
        }

        if (site?.attributes?.googleTagManagerContainerId) {
            if (isPrefetch()) {
                return;
            }

            // Datalayer is only added if Google Tag Manager Container ID is set in the CMS
            window.dataLayer?.push({
                event: LEAD_FORM_SUBMITTED_EVENT_NAME,
                landingPage: source,
                provider: sessionProvider,
            });
        }

        const { fbp, fbc } = getFacebookCookies();
        const allData = {
            href: source,
            site,
            userFormData: cookieFormData,
            // This function is always expected to be called on the offer page itself,
            // which means that the offer page redirect URL is the same as the current URL
            // but allow it to still be provided in case of future changes
            offerPageRedirectUrl: offerPageRedirectUrl || window.location.href,
            ckm_request_id: window.ckm_request_id,
            fbp: fbp,
            fbc: fbc,
            twclid: twclid,
            landingPageCacheKey: landingPageCacheKey,
            origin: "socials",
        };

        sendBeaconOrFetch("/api/form/leadForm", allData, "leadForm-socials");

        signOut({ redirect: false });
    }
};
